// ─────────────────────────────────────────────────────────────────────────────
// AtmosphereBackground — full-screen homepage atmosphere (Chunk 5, desktop).
//
// CSS port of the iOS RexyHomeAtmosphere (Skia). VISUAL-ONLY: a fixed, behind-
// everything, pointer-events-none backdrop driven by a single `warmth` prop (0..1,
// VoiceState-derived). It owns no session/voice state and never intercepts input.
//
// Skia mixes COOL→WARM per-stop on the GPU; CSS can't animate gradient stops
// cheaply, so the desktop port stacks TWO radial layers and crossfades OPACITY:
//   • cool layer — always on, opacity 1
//   • warm layer — opacity = smoothstep(0.45, 0.95, warmth), transition 0.6s
// so idle / listening / user-speaking stay cool and only thinking / agent-speaking
// warm up. Geometry mirrors iOS byte-for-byte: a circle of radius 0.85·max(vw,vh)
// (→ 85vmax) centred at (50%, 34%) with stops [0, 0.42, 1.0]. All constants come
// from lib/atmosphere.ts — none are duplicated here.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  ATMOSPHERE_CENTER_X,
  ATMOSPHERE_CENTER_Y,
  ATMOSPHERE_RADIUS,
  ATMOSPHERE_STOPS,
  ATMOSPHERE_FALLBACK_BG,
  COOL_CENTER,
  COOL_MID,
  COOL_OUTER,
  WARM_CENTER,
  WARM_MID,
  WARM_OUTER,
  clamp01,
  smoothstep,
  rgba,
  RGBA,
} from '../lib/atmosphere';

// Build a CSS radial-gradient mirroring the iOS Skia radial: circle of radius
// 0.85·max(vw,vh) at (50%, 34%), three colour stops at [0, 0.42, 1.0].
function radial(center: RGBA, mid: RGBA, outer: RGBA): string {
  const cx = ATMOSPHERE_CENTER_X * 100;
  const cy = ATMOSPHERE_CENTER_Y * 100;
  const r = ATMOSPHERE_RADIUS * 100; // vmax === max(vw, vh), so 85vmax === 0.85·maxDim
  const [s0, s1, s2] = ATMOSPHERE_STOPS;
  return (
    `radial-gradient(circle ${r}vmax at ${cx}% ${cy}%, ` +
    `${rgba(center)} ${s0 * 100}%, ` +
    `${rgba(mid)} ${s1 * 100}%, ` +
    `${rgba(outer)} ${s2 * 100}%)`
  );
}

// Static — neither gradient depends on warmth (only the warm layer's opacity does).
const COOL_GRADIENT = radial(COOL_CENTER, COOL_MID, COOL_OUTER);
const WARM_GRADIENT = radial(WARM_CENTER, WARM_MID, WARM_OUTER);

const rootStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  // D3: zIndex 0 (was -1). At -1 it escaped behind RexyHomepage's opaque root fill;
  // at 0 it paints above the root base and below the content layers (zIndex 1+).
  zIndex: 0,
  overflow: 'hidden',
  pointerEvents: 'none',
  backgroundColor: ATMOSPHERE_FALLBACK_BG, // paints before/around the gradients (no flash)
};

const coolStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  backgroundImage: COOL_GRADIENT,
};

const warmStyleBase: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  backgroundImage: WARM_GRADIENT,
  transition: 'opacity 0.6s',
  willChange: 'opacity',
};

export interface AtmosphereBackgroundProps {
  warmth: number; // 0..1, VoiceState-derived
}

export default function AtmosphereBackground({ warmth }: AtmosphereBackgroundProps) {
  const warmOpacity = smoothstep(0.45, 0.95, clamp01(warmth));
  return (
    <div aria-hidden style={rootStyle}>
      <div style={coolStyle} />
      <div style={{ ...warmStyleBase, opacity: warmOpacity }} />
    </div>
  );
}
